import classNames from 'classnames/bind';
import Box from 'src/components/ui-kit/box/box';
import { ButtonSize, ButtonVariant } from 'src/components/ui-kit/button/button';
import ButtonBlock from 'src/components/ui-kit/button/__block';
import FormCheckbox from 'src/components/ui-kit/form/_checkbox';
import FormTextInput from 'src/components/ui-kit/form/_text-input';
import Icon, { IconTheme, IconVariant } from 'src/components/ui-kit/icon/icon';
import { useFormik } from 'formik';
import { CreateAccountForm } from 'src/forms/create-account';
import { DetailedHTMLProps, HTMLAttributes, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import styles from './create-account.module.scss';
import { SignUpPageActions } from 'src/store/pages/sign-up/actions';
import { useDispatch, useSelector } from 'react-redux';
import { SignUpPageSelectors } from 'src/store/pages/sign-up';
import FormTextInputPhone from 'src/components/ui-kit/form/_text-input__phone';
import FormTextInputZip from 'src/components/ui-kit/form/_text-input__zip';
import useServerError from 'src/hooks/use-server-error';
import Link from 'next/link';
import { useReCaptcha } from 'next-recaptcha-v3';

const classesContext = classNames.bind(styles);

export default function CreateAccount({
  className,
  ...restProps
}: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>): JSX.Element {
  const { t } = useTranslation(['components:pages:sign-up:create-account']);

  const dispatch = useDispatch();
  const isSending = useSelector(SignUpPageSelectors.isSubmitting);
  const error = useSelector(SignUpPageSelectors.error);

  const { executeRecaptcha } = useReCaptcha();

  const initialValues = new CreateAccountForm();
  const validationSchema = CreateAccountForm.getValidationSchema(t);

  const handleSubmit = useCallback(
    async (values: CreateAccountForm) => {
      const reCaptchaToken = await executeRecaptcha('signup');

      dispatch(SignUpPageActions.register({ registerData: { ...values, reCaptchaToken } }));
    },
    [executeRecaptcha]
  );

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    validationSchema
  });

  useServerError<CreateAccountForm>(formik, error);

  return (
    <Box
      className={classesContext(['createaccount', className])}
      {...restProps}>
      <h3 className={styles.createaccount_title}>{t('TEXT_CREATE_ACCOUNT')}</h3>
      <form
        onSubmit={formik.handleSubmit}
        className={styles.createaccount_form}>
        <FormTextInput<CreateAccountForm>
          name='email'
          placeholder={t('FORM.TEXT_EMAIL')}
          type='email'
          isAsteriskShown
          formik={formik}
        />
        <FormTextInput<CreateAccountForm>
          name='username'
          placeholder={t('FORM.TEXT_USERNAME')}
          isAsteriskShown
          autoComplete='off'
          formik={formik}
        />
        <FormTextInput<CreateAccountForm>
          name='password'
          placeholder={t('FORM.TEXT_PASSWORD')}
          type='password'
          autoComplete='new-password'
          isAsteriskShown
          formik={formik}
        />
        <FormTextInput<CreateAccountForm>
          name='confirm'
          placeholder={t('FORM.TEXT_CONFIRM_PASSWORD')}
          type='password'
          autoComplete='off'
          isAsteriskShown
          formik={formik}
        />
        <FormTextInput
          name='company'
          placeholder={t('FORM.TEXT_COMPANY')}
          formik={formik} />
        <FormTextInput<CreateAccountForm>
          name='firstName'
          placeholder={t('FORM.TEXT_LAST_NAME')}
          isAsteriskShown
          formik={formik}
        />
        <FormTextInput<CreateAccountForm>
          name='lastName'
          placeholder={t('FORM.TEXT_FIRST_NAME')}
          isAsteriskShown
          formik={formik}
        />
        <FormTextInput<CreateAccountForm>
          name='address'
          placeholder={t('FORM.TEXT_ADDRESS')}
          isAsteriskShown
          formik={formik}
        />
        <FormTextInput<CreateAccountForm>
          name='city'
          placeholder={t('FORM.TEXT_CITY')}
          isAsteriskShown
          formik={formik}
        />
        <FormTextInput<CreateAccountForm>
          name='state'
          placeholder={t('FORM.TEXT_STATE')}
          isAsteriskShown
          formik={formik}
        />
        <FormTextInputZip<CreateAccountForm>
          name='zipCode'
          placeholder={t('FORM.TEXT_ZIP_CODE')}
          isAsteriskShown
          formik={formik}
        />
        <FormTextInputPhone<CreateAccountForm>
          name='phone'
          placeholder={t('FORM.TEXT_PHONE')}
          isAsteriskShown
          formik={formik}
        />
        <FormCheckbox
          name='isAgree'
          formik={formik}
          className={styles.createaccount_form_checkbox}>
          {t('FORM.TEXT_I_AGREE_TO_THE')}{' '}
          <Link href='/pages/index/terms'>
            <a
              target='_blank'
              rel='noopener noreferrer'>
              {t('FORM.TEXT_TERMS_AND_CONDITIONS')}
            </a>
          </Link>
        </FormCheckbox>
        <div className={styles.createaccount_actions}>
          <ButtonBlock
            size={ButtonSize.LARGE}
            variant={ButtonVariant.SECONDARY}
            type='submit'
            disabled={!formik.isValid && !!formik.submitCount}
            isLoading={isSending}
          >
            {t('FORM.BUTTON_CREATE_ACCOUNT')}&nbsp;
            <span className={styles.createaccount_action_icon}>
              {!isSending && <Icon
                variant={IconVariant.ARROW}
                theme={IconTheme.WHITE} />}
            </span>
          </ButtonBlock>
        </div>
      </form>
    </Box>
  );
}
